<script setup>
/**
 * FormControlModulecomponentsTableName
 * Composition-based wrapper around the standard text control.
 * Automatically translates human-readable model titles into database-safe
 * snake_case schema naming conventions in real-time.
 */
import { computed, watch, onMounted } from 'vue';
import FormControlTypeText from '../form-control-type/FormControlTypeText.vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

// --- Computed ---

/**
 * Monitors the baseline user-facing title field value.
 */
const titleValue = computed(() => props.currentData?.title || '');

// --- Methods ---

/**
 * Sanitizes input text into a strict database-compliant table naming format.
 * Strips single quotes, converts invalid symbols to underscores, maps to lowercase,
 * and collapses sequential spaces into unique single underscores.
 */
const formatValue = (str) => {
    if (!str) return '';
    return str
        .replace("'", "")
        .replace(/[^a-zA-Z 0-9_]+/g, '_')
        .toLowerCase()
        .replace(/ +/g, '_');
};

// --- Watchers & Lifecycle ---

/**
 * Sanitizes and applies any standalone incoming value changes to the target data layout.
 */
watch(() => props.value, (val) => {
    if (props.field?.name) {
        props.currentData[props.field.name] = formatValue(val);
    }
});

/**
 * Dual-state title watcher. Automatically calculates cascading slug updates
 * only if the current table name is unchanged from its last automated modification
 * or if it sits completely blank.
 */
watch(titleValue, (newVal, oldVal) => {
    if (!props.field?.name) return;

    const currentTableName = props.currentData[props.field.name];

    if (currentTableName === formatValue(oldVal) || currentTableName === '') {
        props.currentData[props.field.name] = formatValue(newVal);
    }
});

onMounted(() => {
    // If mounting into an empty layout field state, seed the safe formatted schema text immediately
    if (props.field?.name && !props.currentData[props.field.name] && titleValue.value) {
        props.currentData[props.field.name] = formatValue(titleValue.value);
    }
});
</script>

<template>
    <FormControlTypeText
        v-if="!field.hidden"
        v-bind="$props"
        v-on="$listeners"
    />
</template>

<style scoped>
/* Inherits formatting structural rules seamlessly from FormControlTypeText */
</style>